/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useMemo, useState } from 'react'

import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi'
import { TbGasStation, TbTags } from 'react-icons/tb'
import { Link, Navigate } from 'react-router-dom'

import { Alert, Badge, Button, ConfirmModal, TableSkeleton, Toast } from '@/components/ui'
import { ListCounts, ListPageToolbar } from '@/components/ui/list-page/ListPageToolbar'
import { ListSearch } from '@/components/ui/list-page/ListSearch'
import { useApi, useAuth, useToast } from '@/hooks'
import { firstAppPath } from '@/utils/appHome'

import { CategoryFormModal } from './CategoryFormModal'

import { deleteGeofenceCategory, writeFailure, type WriteFailure } from '../api'
import { CompanyPicker } from '../components/CompanyPicker'
import { countByCategory } from '../filters'
import { useGeofenceCategories, useGeofenceCompany, useInvalidateGeofences, useManagedGeofences } from '../hooks'
import { type GeofenceCategory, LOW_FUEL_CATEGORY_TYPE_ID } from '../types'

/**
 * Geofence Categories (`/geofence-categories`): one company's categories, how many geofences use
 * each (a link to that filtered list), and creating, renaming and deleting them in place. Refresh's
 * list / view / edit screens collapse into one table, since a category is just a name and a type.
 *
 * Gated on the `geofences` app grant, like the Geofences page.
 */
export const GeofenceCategoriesPage = () => {
  const { hasPermission } = useAuth()
  const { api } = useApi()
  const [companyId, setCompanyId] = useGeofenceCompany()
  const categoriesQuery = useGeofenceCategories(companyId)
  const geofencesQuery = useManagedGeofences(companyId)
  const invalidate = useInvalidateGeofences()
  const { toast, show, showToast, hideToast } = useToast()

  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<GeofenceCategory | 'new'>()
  const [deleting, setDeleting] = useState<GeofenceCategory>()
  const [isDeleting, setIsDeleting] = useState(false)

  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data])
  const counts = useMemo(() => countByCategory(geofencesQuery.data ?? []), [geofencesQuery.data])
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return categories
      .filter((c) => !term || c.name.toLowerCase().includes(term))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
  }, [categories, search])

  if (!hasPermission('geofences')) {
    return <Navigate to={firstAppPath(hasPermission)} replace />
  }

  const reportFailure = (failure: WriteFailure, action: 'Saving' | 'Deleting') =>
    showToast(
      failure.kind === 'unavailable'
        ? {
            header: 'Not available yet',
            body: `${action} geofence categories isn’t available in Insights yet. Use Refresh for now.`,
            position: 'end',
            info: true,
          }
        : {
            header: `${action === 'Saving' ? 'Not saved' : 'Not deleted'}`,
            body: failure.message,
            position: 'end',
            warning: true,
          }
    )

  const confirmDelete = async () => {
    if (!deleting) return
    setIsDeleting(true)
    try {
      await deleteGeofenceCategory(api, deleting.id)
      await invalidate()
      showToast({ header: 'Category deleted', body: `“${deleting.name}” was deleted.`, position: 'end', success: true })
    } catch (ex) {
      reportFailure(writeFailure(ex), 'Deleting')
    } finally {
      setIsDeleting(false)
      setDeleting(undefined)
    }
  }

  const deletingCount = deleting ? (counts.get(deleting.id) ?? 0) : 0
  const isLoading = !!companyId && categoriesQuery.isLoading

  return (
    <div className="flex h-full min-h-0 flex-col p-4">
      <ListPageToolbar
        title="Geofence Categories"
        sheetIsOpen={false}
        savedViews={null}
        search={
          <ListSearch
            id="search-geofence-categories"
            placeholder="Search categories..."
            value={search}
            onChange={setSearch}
          />
        }
        counts={
          <ListCounts
            count={shown.length.toLocaleString() + ' of ' + categories.length.toLocaleString() + ' categories'}
          />
        }
        scope={<CompanyPicker companyId={companyId} onChange={setCompanyId} />}
        listControls={null}
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary shrink-0"
            disabled={!companyId}
            onClick={() => setEditing('new')}
            data-testid="new-geofence-category"
          >
            <FiPlus className="h-4 w-4" />
            New Category
          </button>
        }
      />

      <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-box border border-base-300 bg-base-100">
        {!companyId ? (
          <EmptyState title="Choose a company" text="Pick a company above to see its geofence categories." />
        ) : isLoading ? (
          <div className="p-2">
            <TableSkeleton columns={4} rows={8} rowHeight={44} />
          </div>
        ) : categoriesQuery.isError ? (
          <div className="p-4">
            <Alert
              type="error"
              message="Couldn’t load categories"
              description="Please try again."
              alertActions={
                <Button variation="outline" size="sm" handleClick={() => void categoriesQuery.refetch()}>
                  Try again
                </Button>
              }
            />
          </div>
        ) : categories.length === 0 ? (
          <EmptyState
            title="No categories yet"
            text="Categories group geofences, such as depots or fuel stations, so alerts and filters can use them together."
            action={
              <Button variation="primary" size="sm" icon={<FiPlus />} handleClick={() => setEditing('new')}>
                New category
              </Button>
            }
          />
        ) : shown.length === 0 ? (
          <EmptyState title="No categories match" text={`Nothing is called “${search}”.`} />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="table table-sm">
              <thead className="sticky top-0 z-10 bg-primary text-primary-content">
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th className="text-right">Geofences</th>
                  <th className="w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((category) => {
                  const count = counts.get(category.id) ?? 0
                  return (
                    <tr key={category.id} className="hover:bg-base-200/60">
                      <td className="font-medium">{category.name}</td>
                      <td>
                        {category.typeId === LOW_FUEL_CATEGORY_TYPE_ID ? (
                          <Badge variant="warning" size="sm" className="gap-1">
                            <TbGasStation /> Low fuel
                          </Badge>
                        ) : (
                          <span className="text-base-content/40">—</span>
                        )}
                      </td>
                      <td className="text-right">
                        {geofencesQuery.isLoading ? (
                          <span className="loading loading-spinner loading-xs" aria-label="Counting" />
                        ) : count > 0 ? (
                          <Link
                            className="link link-primary"
                            to={{ pathname: '/geofences', search: `companyId=${companyId}&category=${category.id}` }}
                          >
                            {count.toLocaleString()}
                          </Link>
                        ) : (
                          <span className="text-base-content/50">0</span>
                        )}
                      </td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs btn-square"
                            aria-label={`Edit ${category.name}`}
                            title="Edit"
                            onClick={() => setEditing(category)}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs btn-square text-error"
                            aria-label={`Delete ${category.name}`}
                            title="Delete"
                            onClick={() => setDeleting(category)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {companyId && (
        <CategoryFormModal
          show={!!editing}
          companyId={companyId}
          category={editing === 'new' ? undefined : editing}
          categories={categories}
          onClose={() => setEditing(undefined)}
          onSaved={(saved) => {
            setEditing(undefined)
            showToast({ header: 'Category saved', body: `“${saved.name}” was saved.`, position: 'end', success: true })
          }}
          onFailed={(failure) => {
            setEditing(undefined)
            reportFailure(failure, 'Saving')
          }}
        />
      )}
      <ConfirmModal
        show={!!deleting}
        title="Delete category"
        variation="danger"
        confirmLabel="Delete"
        isLoading={isDeleting}
        body={
          <div className="flex flex-col gap-2">
            <p>
              Delete <strong>{deleting?.name}</strong>?
            </p>
            {deletingCount > 0 && (
              <p className="text-sm text-base-content/70">
                {deletingCount === 1 ? '1 geofence uses' : `${deletingCount.toLocaleString()} geofences use`} this
                category.
              </p>
            )}
          </div>
        }
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleting(undefined)}
      />
      <Toast {...toast} show={show} onClose={hideToast} />
    </div>
  )
}

const EmptyState = ({ title, text, action }: { title: string; text: string; action?: React.ReactNode }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
    <TbTags className="text-4xl text-base-content/30" />
    <p className="text-lg font-medium">{title}</p>
    <p className="max-w-sm text-sm text-base-content/60">{text}</p>
    {action}
  </div>
)

export default GeofenceCategoriesPage
